import type {
  GroupedPlayerRewardDto,
  RewardTypeValue,
} from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { GamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';

export interface UnclaimedGamificationRewardsCount {
  totalRewards: number;
  sidebarCountRewards: number;
  statusUpRewards: number;
  levelUpRewards: number;
  countOfRewardsWithoutDaily: number;
  countORewardsWithoutDailyAndReferral: number;
}

export type RewardCountExclusions = Record<
  'totalRewards' | 'sidebarCountRewards',
  ReadonlySet<RewardTypeValue>
>;

export interface GetUnclaimedGamificationRewardsCountParams {
  groupedRewards?: GroupedPlayerRewardDto[];
  availableDailyRewardsCount?: number;
  exclusions?: RewardCountExclusions;
}

// Configure exclusions independently for each count. Add a reward type to
// `totalRewards` or `sidebarCountRewards` when it should not contribute to that count.
export const REWARD_COUNT_EXCLUSIONS: RewardCountExclusions = {
  // Example: new Set([GamificationRewardType.Referral])
  totalRewards: new Set(),
  sidebarCountRewards: new Set([GamificationRewardType.Referral]),
};

const countRewards = (
  rewardsByType: ReadonlyMap<RewardTypeValue, number>,
  excludedTypes: ReadonlySet<RewardTypeValue>,
) => [...rewardsByType.entries()]
  .filter(([type]) => !excludedTypes.has(type))
  .reduce((count, [, amount]) => count + amount, 0);

export const getUnclaimedGamificationRewardsCount = (
  {
    groupedRewards = [],
    availableDailyRewardsCount = 0,
    exclusions = REWARD_COUNT_EXCLUSIONS,
  }: GetUnclaimedGamificationRewardsCountParams = {},
): UnclaimedGamificationRewardsCount => {
  const rewardsByType = new Map<RewardTypeValue, number>();
  let statusUpRewards = 0;
  let levelUpRewards = 0;

  groupedRewards.forEach(({ type, availableAmount }) => {
    rewardsByType.set(type, (rewardsByType.get(type) ?? 0) + availableAmount);

    if (type === GamificationRewardType.StatusUp) {
      statusUpRewards += availableAmount;
    }

    if (type === GamificationRewardType.LevelUp) {
      levelUpRewards += availableAmount;
    }
  });

  const countOfRewardsWithoutDaily = countRewards(
    rewardsByType,
    exclusions.totalRewards,
  );

  const countOfSidebarCountRewards = countRewards(
    rewardsByType,
    exclusions.sidebarCountRewards,
  ) + availableDailyRewardsCount;

  const countOfRewardsWithoutDailyAndReferral = countRewards(
    rewardsByType,
    new Set([...exclusions.totalRewards, GamificationRewardType.Referral]),
  );

  return {
    totalRewards: countOfRewardsWithoutDaily + availableDailyRewardsCount,
    sidebarCountRewards: countOfSidebarCountRewards,
    statusUpRewards,
    levelUpRewards,
    countOfRewardsWithoutDaily,
    countORewardsWithoutDailyAndReferral: countOfRewardsWithoutDailyAndReferral,
  };
};
